import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Overlay from './components/common/Overlay';
import { EmptyProductData, ProductData, ProductMetaData, TransitionalVideo } from './interfaces/components';
import Instructions from './pages/Instructions';
import TransitionVideo from './pages/TransitionVideo';
import ExampleTransitionVideo from './assets/videos/Intro.mp4';
import PDP from './pages/PDP';
import { dispatchAudioAnalytics, dispatchStartTagManagerAnalytics, dispatchCTAClickObject } from './Analytics';
import AudioToggleModal from './pages/AudioToggleModal';
import backgroundAudio from './assets/audios/background.mp3';
import Welcome from './pages/Welcome';
import Spinner from './components/common/Spinner';
import axios from 'axios';
import OutOfStock from './pages/OutOfStock';
import { getBrowserName, getOpSys } from './utils/helper';
import SpreadJoy from './pages/SpreadJoy';
import Toast from './components/common/Toast';
import Map from './pages/Map';
import RoseCollection from './components/Modal/CollectionIntro/RoseCollection';
import PivoineFloraCollection from './components/Modal/CollectionIntro/PivoineFloraCollection';
import NeroliOrchideeCollection from './components/Modal/CollectionIntro/NeroliOrchideeCollection';
import NeroliOrchideeBestSellerModal from './components/Modal/CollectionIntro/NeroliOrchideeBestSellerModal';
import MenCollection from './components/Modal/CollectionIntro/MenCollection';
import CherryBlossomCollection from './components/Modal/CollectionIntro/CherryBlossomCollection';
import ArlesienneCollection from './components/Modal/CollectionIntro/ArlesienneCollection';
import WhiteLavenderCollection from './components/Modal/CollectionIntro/WhiteLavenderCollection';
import EauDIparieCollection from './components/Modal/CollectionIntro/EauDIparieCollection';
import AmbreCollection from './components/Modal/CollectionIntro/AmbreCollection';
import VerbenaCollection from './components/Modal/CollectionIntro/VerbenaCollection';
import Intense86Collection from './components/Modal/CollectionIntro/Intense86Collection';

function App() {
  //Refs
  const audioRef = useRef<HTMLAudioElement>(null);

  //States
  const [activeLocale, setActiveLocale] = useState('en');
  const [activeScene, setActiveScene] = useState('loccitane_final_greenhouse_0');
  const [audioActive, setAudioActive] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [tabVisible, setTabVisible] = useState<boolean>(true);
  const [welcomeActive, setWelcomeActive] = useState(true);
  const [instructionsActive, setInstructionsActive] = useState(false);
  const [spreadJoyActive, setSpreadJoyActive] = useState(false);
  const [mapActive, setMapActive] = useState({ type: '', active: false });

  const [toastActive, setToastActive] = useState({ active: false, text: '' });

  const [oosActive, setOosActive] = useState(false);
  const [pdpLoading, setPdpLoading] = useState(false);
  const [pdpActive, setPDPActive] = useState(false);
  const [pdpData, setPDPData] = useState<ProductData>();
  const [productMetaData, setProductMetaData] = useState<ProductMetaData>();

  const [collectionIntoModal, setCollectionIntoModal] = useState({
    rose: false,
    pivoineFlora: false,
    neroliOrchidee: false,
    neroliOrchideeDualPDP: false,
    menCollection: false,
    cherryBlossom: false,
    arlesienne: false,
    whiteLavender: false,
    eauDIparie: false,
    amber: false,
    verbena: false,
    intense86: false,
  });

  const [transitionVideo, setTransitionVideo] = useState<TransitionalVideo>({
    videoURL: ExampleTransitionVideo,
    active: false,
    gotoRoom: 'room-1',
  });

  const [isUserFirstTime, setIsUserFirstTime] = useState<boolean>(true);

  useLayoutEffect(() => {
    const userVisited = localStorage.getItem('userVisited');
    if (userVisited) {
      setIsUserFirstTime(false);
      setWelcomeActive(false);
      setInstructionsActive(false);
      setAudioModal(true);
    } else {
      localStorage.setItem('userVisited', 'true');
    }
  }, []);

  useEffect(() => {
    const locale = window.locale;
    if (locale === 'ar-ae' || locale === 'ar-sa' || locale === 'ar-kw' || locale === 'ar-qa') {
      setActiveLocale('ar');
    } else if (locale === 'en-ae' || locale === 'en-sa' || locale === 'en-kw' || locale === 'en-qa') {
      setActiveLocale('en');
    }
  }, []);

  const playTransitionVideo = (videoName: string, room: string) => {
    const videoURLs = {
      videoOne: ExampleTransitionVideo,
      videoTwo: ExampleTransitionVideo,
      videoThree: ExampleTransitionVideo,
    };

    setTransitionVideo({
      active: true,
      videoURL: videoURLs[videoName as keyof typeof videoURLs],
      gotoRoom: room,
    });
  };

  const setProductMetaDetails = (roomName: string) => {
    const orgIdMetaTag = document.querySelector('meta[name=org_id]');
    const experienceNameMetaTag = document.querySelector('meta[name=experience_name]');
    let organisationName: string | null = '';
    let experienceName: string | null = '';
    if (orgIdMetaTag) {
      organisationName = orgIdMetaTag.getAttribute('content');
    }
    if (experienceNameMetaTag) {
      experienceName = experienceNameMetaTag.getAttribute('content');
    }
    setProductMetaData({
      organisationName: organisationName,
      experienceName: experienceName,
      roomName: roomName,
      browser: getBrowserName(),
      os: getOpSys(),
    });
  };

  const openProductPopUp = (productId: string, collection: string, scene: string) => {
    dispatchCTAClickObject('loccitane', 'button', 'productOpen', productId, '');
    console.log('Product', productId, collection);
    const roomName: string = scene.split('_')[0];
    setProductMetaDetails(roomName);
    let apiLocale = '';

    if (window.locale === 'ar-ae') {
      apiLocale = 'ar-ae';
    } else if (window.locale === 'ar-sa') {
      apiLocale = 'ar-sa';
    } else if (window.locale === 'en-sa') {
      apiLocale = 'en-sa';
    } else if (window.locale === 'en-kw') {
      apiLocale = 'en-kw';
    } else if (window.locale === 'ar-kw') {
      apiLocale = 'ar-kw';
    } else if (window.locale === 'en-qa') {
      apiLocale = 'en-qa';
    } else if (window.locale === 'ar-qa') {
      apiLocale = 'ar-qa';
    } else {
      apiLocale = 'en-ae';
    }

    if (productId !== null) {
      setPdpLoading(true);
      console.log('PDP is loadingggg');
      axios
        .get(process.env.REACT_APP_API_URL + `${apiLocale}/` + productId, {
          params: {
            'include-data': false,
          },
        })
        .then((res) => {
          console.log(res);

          const data = res.data.data as ProductData;

          if (
            window.locale === 'ar-ae' ||
            window.locale === 'ar-sa' ||
            window.locale === 'ar-qa' ||
            window.locale === 'ar-kw'
          ) {
            setPDPData({ ...data, collection: 'لوكسيتان' });
          } else {
            setPDPData({ ...data, collection: "L'Occitane" });
          }
        })
        .catch((err: unknown) => {
          console.log(err);
          setPdpLoading(false);
          setOosActive(true);
        });
    } else {
      setOosActive(true);
    }
  };

  useEffect(() => {
    if (pdpData && pdpData.title !== '') {
      setPdpLoading(false);
      setPDPActive(true);
    }
  }, [pdpData]);

  const updateActiveScene = (sceneName: string) => {
    setActiveScene(sceneName);
  };

  const openSignPostMap = () => {
    setMapActive({ type: 'signPost', active: true });
  };

  const openRoseCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'roseCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, rose: true });
  };

  const openPivoineFloraCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'pivoineFloraCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, pivoineFlora: true });
  };

  const openNeroliOrchideeCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'neroliOrchideeCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, neroliOrchidee: true });
  };

  const openNeroliOrchideeDualPDP = () => {
    dispatchCTAClickObject('loccitane', 'button', 'neroliOrchideeDualPDP', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, neroliOrchideeDualPDP: true });
  };

  const openMenCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'menCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, menCollection: true });
  };

  const openCherryBlossomCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'cherryBlossomCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, cherryBlossom: true });
  };

  const openArlesienneCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'arlesienneCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, arlesienne: true });
  };

  const openWhiteLavenderCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'whiteLavenderCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, whiteLavender: true });
  };

  const openEauDIparieCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'eauDIparieCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, eauDIparie: true });
  };

  const openAmbreCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'ambreCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, amber: true });
  };

  const openVerbenaCollection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'verbenaCollection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, verbena: true });
  };

  const openIntense86Collection = () => {
    dispatchCTAClickObject('loccitane', 'button', 'intense86Collection', 'open', '');
    setCollectionIntoModal({ ...collectionIntoModal, intense86: true });
  };

  const openEnteranceMap = () => {
    setMapActive({ type: 'enterance', active: true });
    dispatchCTAClickObject('loccitane', 'button', 'enteranceMap', 'open', '');
  };

  useEffect(() => {
    window.openProductPopUp = openProductPopUp;
    window.playTransitionVideo = playTransitionVideo;
    window.updateScene = updateActiveScene;
    window.openSignPostMap = openSignPostMap;
    window.openRoseCollection = openRoseCollection;
    window.openPivoineFloraCollection = openPivoineFloraCollection;
    window.openNeroliOrchideeCollection = openNeroliOrchideeCollection;
    window.openNeroliOrchideeDualPDP = openNeroliOrchideeDualPDP;
    window.openMenCollection = openMenCollection;
    window.openCherryBlossomCollection = openCherryBlossomCollection;
    window.openArlesienneCollection = openArlesienneCollection;
    window.openWhiteLavenderCollection = openWhiteLavenderCollection;
    window.openEauDIparieCollection = openEauDIparieCollection;
    window.openAmbreCollection = openAmbreCollection;
    window.openVerbenaCollection = openVerbenaCollection;
    window.openIntense86Collection = openIntense86Collection;
    window.openEnteranceMap = openEnteranceMap;
  }, []);

  // EventListeners
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') setTabVisible(true);
    else setTabVisible(false);
  });

  useEffect(() => {
    if (audioActive && !tabVisible) {
      if (audioRef.current) {
        dispatchAudioAnalytics(false, 'loccitane');
        audioRef.current.pause();
        setAudioActive(false);
      }
    }
  }, [tabVisible]);

  const toggleAudio = () => {
    if (!audioActive) {
      if (audioRef.current) {
        dispatchAudioAnalytics(true, 'loccitane');
        audioRef.current.play();
        setAudioActive(true);
      }
    } else {
      if (audioRef.current) {
        dispatchAudioAnalytics(false, 'loccitane');
        audioRef.current.pause();
        setAudioActive(false);
      }
    }
  };

  const playInitialAudio = (state: boolean) => {
    if (audioRef.current) audioRef.current.volume = 0.5;

    if (state) {
      if (audioRef.current) {
        audioRef.current.play();
        setAudioActive(true);
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        setAudioActive(false);
      }
    }
  };

  return (
    <>
      <Toast
        activeLocale={activeLocale}
        position="absolute"
        active={toastActive.active}
        text={toastActive.text}
        setActive={(state: boolean) => setToastActive({ ...toastActive, active: state })}
      />

      <AudioToggleModal
        activeLocale={activeLocale}
        proceedAudio={(state: boolean) => {
          dispatchAudioAnalytics(state, 'loccitane');
          playInitialAudio(state);
          setAudioModal(false);
          isUserFirstTime && setInstructionsActive(true);
        }}
        active={audioModal}
      />
      <audio ref={audioRef} src={backgroundAudio} />

      <Overlay
        audioActive={audioActive}
        toggleAudio={toggleAudio}
        activeLocale={activeLocale}
        closeAllUI={() => {
          setInstructionsActive(false);
        }}
        activeScene={activeScene}
        toggleInstructions={() => {
          const action = instructionsActive ? 'open' : 'close';
          dispatchCTAClickObject('loccitane', 'button', 'toggleInstructions', action, '');
          setInstructionsActive(!instructionsActive);
        }}
        setLocale={(locale) => {
          dispatchCTAClickObject('loccitane', 'button', 'toggleLocale', locale, '');
          setActiveLocale(locale);

          if (locale === 'en') {
            if (window.originalLocale === 'en-ae') {
              window.locale = 'en-ae';
            } else if (window.originalLocale === 'en-sa') {
              window.locale = 'en-sa';
            } else if (window.originalLocale === 'en-kw') {
              window.locale = 'en-kw';
            } else if (window.originalLocale === 'en-qa') {
              window.locale = 'en-qa';
            } else if (window.originalLocale === 'ar-ae') {
              window.locale = 'en-ae';
            } else if (window.originalLocale === 'ar-sa') {
              window.locale = 'en-sa';
            } else if (window.originalLocale === 'ar-kw') {
              window.locale = 'en-kw';
            } else if (window.originalLocale === 'ar-qa') {
              window.locale = 'en-qa';
            }
          } else if (locale === 'ar') {
            console.log(window.originalLocale);

            if (window.originalLocale === 'en-ae') {
              window.locale = 'ar-ae';
            } else if (window.originalLocale === 'en-sa') {
              window.locale = 'ar-sa';
            } else if (window.originalLocale === 'en-kw') {
              window.locale = 'ar-kw';
            } else if (window.originalLocale === 'en-qa') {
              window.locale = 'ar-qa';
            } else if (window.originalLocale === 'ar-ae') {
              window.locale = 'ar-ae';
            } else if (window.originalLocale === 'ar-sa') {
              window.locale = 'ar-sa';
            } else if (window.originalLocale === 'ar-kw') {
              window.locale = 'ar-kw';
            } else if (window.originalLocale === 'ar-qa') {
              window.locale = 'ar-qa';
            }
          }
        }}
        setMapActive={setMapActive}
      />

      {pdpLoading ? (
        <Spinner />
      ) : (
        <>
          <OutOfStock
            activeLocale={activeLocale}
            active={oosActive}
            setActive={(state: boolean) => setOosActive(state)}
          />
          <PDP
            activeLocale={activeLocale}
            pdpData={pdpData}
            active={pdpActive}
            productMetaData={productMetaData as ProductMetaData}
            setToastActive={(text: string) => setToastActive({ active: true, text: text })}
            setActive={(state) => {
              setPDPActive(state);
              setTimeout(() => {
                setPDPData(EmptyProductData);
              }, 500);
            }}
          />
        </>
      )}

      {isUserFirstTime && (
        <Welcome
          activeLocale={activeLocale}
          active={welcomeActive}
          setAudioModal={setAudioModal}
          setActive={() => {
            dispatchStartTagManagerAnalytics('loccitane');
            dispatchCTAClickObject('loccitane', 'button', 'welcome', 'enter', '');
            setWelcomeActive(false);
            setAudioModal(true);
          }}
        />
      )}

      <Map
        active={mapActive.active}
        mapType={mapActive.type}
        activeLocale={activeLocale}
        close={() => {
          setMapActive({ type: '', active: false });
        }}
      />

      {isUserFirstTime && (
        <Instructions
          activeLocale={activeLocale}
          active={instructionsActive}
          setActive={() => {
            setInstructionsActive(false);
            dispatchCTAClickObject('loccitane', 'button', 'instructions', 'close', '');
          }}
        />
      )}
      <SpreadJoy
        activeLocale={activeLocale}
        active={spreadJoyActive}
        setActive={() => {
          setSpreadJoyActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'spreadJoy', 'close', '');
        }}
      />
      <TransitionVideo
        room={transitionVideo.gotoRoom}
        videoURL={transitionVideo.videoURL}
        playing={transitionVideo.active}
        setPlaying={() => {
          setTransitionVideo({ ...transitionVideo, active: false });
        }}
      />
      <RoseCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.rose}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, rose: false });
          dispatchCTAClickObject('loccitane', 'button', 'roseCollection', 'close', '');
        }}
      />
      <PivoineFloraCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.pivoineFlora}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, pivoineFlora: false });
          dispatchCTAClickObject('loccitane', 'button', 'pivoineFloraCollection', 'close', '');
        }}
      />
      <NeroliOrchideeCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.neroliOrchidee}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, neroliOrchidee: false });
          dispatchCTAClickObject('loccitane', 'button', 'neroliOrchideeCollection', 'close', '');
        }}
      />
      <NeroliOrchideeBestSellerModal
        activeLocale={activeLocale}
        active={collectionIntoModal.neroliOrchideeDualPDP}
        openProductPopUp={openProductPopUp}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, neroliOrchideeDualPDP: false });
          dispatchCTAClickObject('loccitane', 'button', 'neroliOrchideeDualPDP', 'close', '');
        }}
      />
      <MenCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.menCollection}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, menCollection: false });
          dispatchCTAClickObject('loccitane', 'button', 'menCollection', 'close', '');
        }}
      />
      <CherryBlossomCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.cherryBlossom}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, cherryBlossom: false });
          dispatchCTAClickObject('loccitane', 'button', 'cherryBlossomCollection', 'close', '');
        }}
      />
      <ArlesienneCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.arlesienne}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, arlesienne: false });
          dispatchCTAClickObject('loccitane', 'button', 'arlesienneCollection', 'close', '');
        }}
      />
      <WhiteLavenderCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.whiteLavender}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, whiteLavender: false });
          dispatchCTAClickObject('loccitane', 'button', 'whiteLavenderCollection', 'close', '');
        }}
      />
      <EauDIparieCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.eauDIparie}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, eauDIparie: false });
          dispatchCTAClickObject('loccitane', 'button', 'eauDIparieCollection', 'close', '');
        }}
      />
      <AmbreCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.amber}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, amber: false });
          dispatchCTAClickObject('loccitane', 'button', 'ambreCollection', 'close', '');
        }}
      />
      <VerbenaCollection
        activeLocale={activeLocale}
        active={collectionIntoModal.verbena}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, verbena: false });
          dispatchCTAClickObject('loccitane', 'button', 'verbenaCollection', 'close', '');
        }}
      />
      <Intense86Collection
        activeLocale={activeLocale}
        active={collectionIntoModal.intense86}
        setActive={() => {
          setCollectionIntoModal({ ...collectionIntoModal, intense86: false });
          dispatchCTAClickObject('loccitane', 'button', 'intense86Collection', 'close', '');
        }}
      />
    </>
  );
}

export default App;
